import { useEffect, useState } from 'react'
import { api } from '../api/client.js'
import type { WhatsAppTemplate } from '../modules/conversations/types.js'

let cache: { items: WhatsAppTemplate[]; fetchedAt: number } | null = null
const STALE_MS = 5 * 60 * 1000

export interface UseWhatsAppTemplatesReturn {
  templates: WhatsAppTemplate[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Fetches the tenant's WhatsApp message templates from
 * GET /admin/channels/whatsapp/templates. Caches at module level for
 * STALE_MS so the composer's picker doesn't refetch on every open.
 */
export function useWhatsAppTemplates(): UseWhatsAppTemplatesReturn {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>(cache?.items ?? [])
  const [loading, setLoading] = useState(cache === null)
  const [error, setError] = useState<string | null>(null)

  async function load(): Promise<void> {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<{ items: WhatsAppTemplate[] }>(
        '/admin/channels/whatsapp/templates',
      )
      cache = { items: resp.items, fetchedAt: Date.now() }
      setTemplates(resp.items)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (cache === null || Date.now() - cache.fetchedAt > STALE_MS) void load()
  }, [])

  return { templates, loading, error, refetch: load }
}
